import { cardsHelper } from './CardReducer';
import BaseCardBehavior from './CardBehavior/BaseCardBehavior';
import { CardTypeMap } from 'Prototype/Level/Card/CardTypeMap';
import { CardTask } from './CardTasks';
import { Random } from 'Utility/Random';
import { AnyBoosterType } from './BoosterType';
import { BoosterTypeMap } from './BoosterTypeMap';
import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BoosterBehavior/BaseBoosterBehavior';
import { CardType } from 'Prototype/Level/Card/CardType';
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from 'Prototype/Level/Card/ICardData';
import { CARD_OFFSET_IN_STACK, CARD_STACK_OFFSET, MAX_OPENED_CARDS_IN_STACK } from 'Constants/InGame';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import CardStateModifierHelper from './CardStateModifierHelper';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { trackCardPlayed } from 'Utility/Tracking/TrackingHelpers/Card';
import AudioStateHelper from '../Audio/AudioStateHelper';
import { IGameState } from '../Game/IGameState';
import {
    cardCastsTo,
    GoldCardData,
    ICardsState,
    ICardStateData,
    ICardStateTaskData,
    StreakReward,
    ValueCardData,
} from './ICardState';
import { JOKER_STREAK_REWARD_CHANCE, STREAK_PROGRESS_STEPS } from 'Constants/Level';
import { cardColorBySuit, getCardSuit, Suit } from 'Utility/CardsHelper';
import { ICardModifierData } from 'Prototype/Level/Card/ICardModifierData';
import { initialCardStateData } from './initialCardStateData';
import Game from 'Game';
import { PlayBoosterOptions } from './PlayBoosterOptions';
import { Logger, LogOrigin, LogStyles } from '../../Utility/Logger/LoggerSuite';
import { JOKER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE } from 'Constants/PreBoosters';
import { ILimit, MathHelper } from 'Utility/MathHelper';
import { CardState } from 'Prototype/Level/Card/CardState';
import { isUndefined } from 'Utility/Types';
import { isLevelFailed } from 'State/Level/LevelStateHelper';

export interface ISetCardInStackRandomlyOptions {
    range?: ILimit;
    forceVisible?: boolean;
}

export default class CardStateHelper {
    public static addCard(state: ICardsState, cardData: ICardStateData): ICardsState {
        state.cards[cardData.id] = cardData;
        return state;
    }

    public static playBooster<T extends AnyBoosterType>(
        state: IGameState,
        type: T,
        options?: PlayBoosterOptions<T> extends IBaseBoosterBehaviorPlayMeta ? PlayBoosterOptions<T> : never
    ): IGameState {
        const behavior = CardStateHelper.getBoosterBehavior(state.levelState.cardsState, type);

        if (options?.force || behavior.isPlayable(state)) {
            state = behavior.play(state, options);

            // Handle card state after booster is played.
            state = CardStateHelper.updateCards(state);

            // Check if cards need to be flipped.
            let cardsState = CardStateHelper.checkCardsObscured(state.levelState.cardsState);

            // Check if next card should be folded.
            //cardsState = CardStateHelper.checkFoldNextCard(cardsState);

            // Update cards after a booster is played.
            state = CardStateHelper.updateGameState(state, cardsState);
        }

        return state;
    }

    public static playCard(state: IGameState, cardID: string): IGameState {
        let cardState: ICardsState = getCardsState(state);
        const cardData = CardStateHelper.getCardStateData(state, cardID);
        if (!cardData) return state;

        const behavior = CardStateHelper.getCardBehavior(cardState, cardID);
        if (behavior.isPlayable(state)) {
            const delayByCardID = behavior.getFlipDelays(cardState);
            state = trackCardPlayed(state);
            state = behavior.play(state);
            // Update card state to new card state
            cardState = getCardsState(state);
            // testing audio delete it later
            AudioStateHelper.cardPlayed(cardState.cards[cardID]);

            // Increment the number of completed 'turns'.
            cardState = { ...cardState, turns: cardState.turns + 1 };

            // Check if cards need to be flipped.
            cardState = CardStateHelper.checkCardsObscured(cardState, delayByCardID);

            // Check if next card should be folded.
            //state = CardStateHelper.checkFoldNextCard(state);
        } else {
            if (CardStateHelper.getCardState(state, cardID) === CardState.IN_PLAY) {
                cardState.cards[cardID].attempts += 1;
                cardState = CardStateHelper.setStateTaskData(cardState, {
                    task: CardTask.WIGGLE,
                    cards: [cardID],
                });
            }
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardState,
            },
        };
    }

    public static updateCard(state: ICardsState, cardID: string, cardData: Partial<ICardStateData>): ICardsState {
        return {
            ...state,
            cards: {
                ...state.cards,
                [cardID]: {
                    ...state.cards[cardID],
                    ...cardData,
                } as ICardStateData,
            },
        };
    }

    public static setCardInPlay(state: ICardsState, cardID: string): ICardsState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        return {
            ...state,
            inPlay: [...state.inPlay, cardID],
        };
    }

    public static setCardInStack(
        state: ICardsState,
        cardID: string,
        options: {
            data?: Partial<ICardStateData>;
            index?: number;
        } = {}
    ): ICardsState {
        const { data = {} } = options;
        state = CardStateHelper.removeFromStateArrays(state, cardID);
        state = CardStateHelper.updateCard(state, cardID, {
            faceUp: false,
            x: 0,
            y: 0,
            rotation: 0,
            ...data,
        });

        return (state = {
            ...state,
            inStack:
                options.index !== undefined
                    ? ArrayHelper.insert(state.inStack, options.index, cardID)
                    : [...state.inStack, cardID],
        });
    }

    public static setCardInStackByIndex(state: ICardsState, cardID: string, indexToAdd: number): ICardsState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);
        state = CardStateHelper.updateCard(state, cardID, {
            rotation: 0,
        });

        return {
            ...state,
            inStack: ArrayHelper.insert(state.inStack, indexToAdd, cardID),
        };
    }

    public static setCardFolded(state: ICardsState, cardID: string): ICardsState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);
        state = CardStateHelper.updateCard(state, cardID, {
            faceUp: true,
            x: 0,
            y: 0,
            rotation: 0,
        });

        return {
            ...state,
            folded: [...state.folded, cardID],
        };
    }

    public static setJokerCardFolded(state: ICardsState, cardID: string): ICardsState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);
        state.cards[cardID] = {
            ...state.cards[cardID],
            faceUp: true,
            rotation: 0,
        };
        state.folded.push(cardID);
        return state;
    }

    public static setCardDiscarded(state: IGameState, ...cardIDs: string[]): IGameState {
        let cardsState = getCardsState(state);

        for (let i = 0; i < cardIDs.length; i++) {
            const cardID = cardIDs[i];
            const cardBehavior = CardStateHelper.getCardBehavior(state.levelState.cardsState, cardID);

            cardsState = CardStateHelper.removeFromStateArrays(cardsState, cardID);
            cardsState = {
                ...cardsState,
                discarded: [...cardsState.discarded, cardID],
            };
            state = CardStateHelper.updateGameState(state, cardsState);
            state = cardBehavior.onCardDiscarded(state);
        }

        return state;
    }

    public static isCardDiscarded(state: IGameState, cardId: string): boolean {
        return state.levelState.cardsState.discarded.includes(cardId);
    }

    public static setJokerPreBoosterCardInStack(state: ICardsState, cardID: string): ICardsState {
        const range: ILimit = JOKER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE;

        // +1 here because the joker card is not in the stack yet
        const stackCount: number = Math.min(MAX_OPENED_CARDS_IN_STACK, state.inStack.length + 1);
        const randomDistanceFromTop: number = Random.int(
            // Min Range: If stack count is < min, then use stack count as the min else use default min
            stackCount < range.min ? stackCount : range.min,
            // Max range: Take which ever is lower, stack count or max range
            Math.min(stackCount, range.max)
        );

        // -1 here to adjust for count -> index
        const index: number = MathHelper.clamp(stackCount - randomDistanceFromTop, 0, stackCount - 1);

        return CardStateHelper.setCardInStack(state, cardID, { index });
    }

    /**
     * Adds the card to the stack a random distance from the top of the deck
     * @param options
     * the range provided will limit the distance from the top of the deck the card can be placed, defaults to the entire stack;
     * forceVisible indicates if the card should be forced to be within the range of cards that are currently visible, defaults to true;
     */
    public static setCardInStackRandomly(
        state: IGameState,
        cardID: string,
        options?: ISetCardInStackRandomlyOptions
    ): IGameState {
        // +1 here because the card is not in the stack yet
        const newLength = state.levelState.cardsState.inStack.length + 1;
        let { range, forceVisible = true } = options ?? {};

        if (!range) {
            range = {
                min: 0,
                max: newLength,
            };
        }

        const stackCount: number = forceVisible ? Math.min(MAX_OPENED_CARDS_IN_STACK, newLength) : newLength;
        const randomDistanceFromTop: number = Random.int(
            // Min Range: If stack count is < min, then use stack count as the min else use default min
            stackCount < range.min ? stackCount : range.min,
            // Max range: Take which ever is lower, stack count or max range
            Math.min(stackCount, range.max)
        );

        // -1 here to adjust for count -> index
        const index: number = MathHelper.clamp(stackCount - randomDistanceFromTop, 0, stackCount - 1);

        return CardStateHelper.updateGameState(
            state,
            CardStateHelper.setCardInStack(state.levelState.cardsState, cardID, { index })
        );
    }

    /**
     * Removes specified card ID from all arrays - overwrites all arrays.
     * @param state
     * @param cardID
     */
    public static removeFromStateArrays(state: ICardsState, cardID: string): ICardsState {
        return {
            ...state,
            inPlay: state.inPlay.filter(value => value !== cardID),
            inStack: state.inStack.filter(value => value !== cardID),
            folded: state.folded.filter(value => value !== cardID),
            discarded: state.discarded.filter(value => value !== cardID),
        };
    }

    public static checkCardsObscured(
        state: ICardsState,
        delayByCardID: Record<string, number> | null = null
    ): ICardsState {
        const flippedCards: string[] = [];

        state.inPlay.forEach(cardID => {
            const behavior = CardStateHelper.getCardBehavior(state, cardID);

            // Set faceUp to true if the card was previously face-down and is no longer obscured.
            if (state.cards[cardID].faceUp === false && !behavior.isObscured(state)) {
                state = CardStateHelper.updateCard(state, cardID, {
                    faceUp: true,
                });

                // Collect cards for flipping animations.
                flippedCards.push(cardID);
            }
        });

        if (flippedCards.length > 0) {
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FLIP,
                cards: flippedCards,
                delayByCardID,
            });
        }

        return state;
    }

    public static checkFoldNextCard(state: ICardsState): ICardsState {
        if (state.inStack.length > 0 && state.folded.length === 0) {
            const cardID = state.inStack[state.inStack.length - 1];
            state = CardStateHelper.setCardFolded(state, cardID);
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FOLD,
                cards: [cardID],
            });
        }

        return state;
    }

    public static getCardState(state: IGameState, cardID: string): CardState {
        const cardState = getCardsState(state);
        if (cardState.inPlay.indexOf(cardID) !== -1) {
            return CardState.IN_PLAY;
        }
        if (cardState.inStack.indexOf(cardID) !== -1) {
            return CardState.IN_STACK;
        }
        if (cardState.folded.indexOf(cardID) !== -1) {
            return CardState.FOLDED;
        }
        if (cardState.discarded.indexOf(cardID) !== -1) {
            return CardState.DISCARDED;
        }
        return CardState.UNKNOWN;
    }

    public static getCardBehavior(state: ICardsState, cardID: string): BaseCardBehavior {
        return new CardTypeMap[state.cards[cardID].type].behavior(cardID);
    }

    public static getBoosterBehavior(state: ICardsState, type: AnyBoosterType): BaseBoosterBehavior {
        return new BoosterTypeMap[type].behavior();
    }

    public static getCurrentCard(state: ICardsState): string {
        return state.folded[state.folded.length - 1];
    }

    /**
     * Update all cards (called after a 'valid' turn).
     * @param state
     */
    public static updateCards(state: IGameState): IGameState {
        do {
            state = CardStateHelper.forEachCard(state, (state, cardID) => {
                return CardStateHelper.getCardBehavior(state.levelState.cardsState, cardID).update(state);
            });
        } while (CardStateHelper.hasActiveModifierType(state, CardModifierType.auto_collect));

        return state;
    }

    public static setStateTaskData(state: ICardsState, taskData: ICardStateTaskData): ICardsState {
        return {
            ...state,
            tasks: [...state.tasks, taskData],
        };
    }

    public static getCardMatches(state: IGameState): string[] {
        const playableCards: string[] = state.levelState.cardsState.inPlay.filter(inPlayCardID => {
            const behavior = CardStateHelper.getCardBehavior(state.levelState.cardsState, inPlayCardID);
            const isPlayable: boolean = behavior.isPlayable(state);
            return isPlayable;
        });
        return playableCards;
    }

    public static plusStreakCards(state: IGameState): ICardsState {
        let cardsState = state.levelState.cardsState;
        if (!cardsState.streakEnabled) {
            return cardsState;
        }

        const lastCardColor = cardsState.matchSequence[cardsState.matchSequence.length - 1];
        const multiplier = cardsState.matchSequence.slice(-5).every(c => c === lastCardColor) ? 2 : 1;
        const targets: string[] = [];

        // if was all same suit, track it
        if (multiplier === 2) {
            track('mStreakColorNum', 1, TrackMethod.ADD);
        }
        track('mStreakNum', 1, TrackMethod.ADD);

        if (cardsState.streakReward === StreakReward.Joker) {
            track('mJokerCardsWon', multiplier, TrackMethod.ADD);

            for (let i = 0; i < multiplier; i++) {
                const newCardID = `card_joker_${Random.ID()}`;
                targets.push(newCardID);

                cardsState = CardStateHelper.addCard(cardsState, {
                    ...initialCardStateData,
                    id: newCardID,
                    type: CardType.joker_pre_booster,
                    faceUp: true,
                    madeByBooster: false,
                });

                cardsState = CardStateHelper.setJokerPreBoosterCardInStack(cardsState, newCardID);
            }

            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.PLUS_STREAK_JOKER,
                cards: targets,
            });
        } else {
            const newCardCount: number = (cardsState.streakReward === StreakReward.OneCard ? 1 : 2) * multiplier;

            track('mExtraCardsWon', newCardCount, TrackMethod.ADD);

            cardsState = this.addNewCards(cardsState, newCardCount, targets);

            // Trigger plus streak cards animation task.
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.PLUS_STREAK,
                cards: targets,
            });
        }

        const canBeJoker = state.levelState.levelNumber > 6 && Math.random() <= JOKER_STREAK_REWARD_CHANCE;

        const nextReward = canBeJoker ? StreakReward.Joker : StreakReward.TwoCards;
        cardsState = CardStateHelper.setStreakReward(cardsState, nextReward);

        return cardsState;
    }

    /**
     * Draw a card from `cardsHelper` that will match an in-play value card. If there are no suitable in-play cards then
     * return `null`
     * @param state
     */
    public static drawInPlayMatch(state: ICardsState): number | null {
        const inPlayCardIDs = this.getCardsInPlay(state, { requireCardTypes: [CardType.value, CardType.gold] }).filter(
            cardID => {
                const behavior = this.getCardBehavior(state, cardID);
                return behavior.isFaceUp(state) && !behavior.isObscured(state);
            }
        );

        if (inPlayCardIDs.length === 0) return null;

        const cardToMatch = Random.getRandomItem(inPlayCardIDs);

        const card = state.cards[cardToMatch];

        if (card.type === CardType.value) {
            return cardsHelper.drawMatch(card.value);
        }

        if (card.type === CardType.gold) {
            return cardsHelper.drawSuitMatch(card.suit);
        }

        return null;
    }

    /**
     *
     * @param state
     * @param amount
     * @param cardIDs
     */
    public static addNewCards(state: ICardsState, amount: number, cardIDs: string[]): ICardsState {
        const cardValues = CardStateHelper.drawNewCardsValues(amount);
        return CardStateHelper.addNewCardsInStack(state, cardValues, cardIDs);
    }

    /**
     * Draw a predetermined number of cards from the deck. Ensure there are no duplicate values. Max number of values
     * possible is 13.
     * @param amount
     */
    public static drawNewCardsValues(amount: number) {
        const cardValues: number[] = [];

        // Store un-suited values of cards to prevent duplicate values.
        const drawnValues: number[] = [];

        for (let i = 0; i < amount; i++) {
            // Draw a new card.
            let value = cardsHelper.draw();

            // If the un-suited value of the card was already drawn and the number of drawn values is less than 13 keep trying.
            // We must check the length of `drawnValues` is less than 13 otherwise there will be an infinite loop.
            while (drawnValues.length < 13 && drawnValues.indexOf(value % 13) !== -1) {
                value = cardsHelper.draw();
            }

            cardValues.push(value);

            // Store the un-suited value of the card.
            drawnValues.push(value % 13);
        }

        return cardValues;
    }

    public static addNewCardsInStack(state: ICardsState, cardValues: number[], cardIDs: string[]) {
        cardValues.forEach(value => {
            const newCardID = `card_${Random.ID()}`;

            state = CardStateHelper.addCard(state, {
                ...initialCardStateData,
                id: newCardID,
                type: CardType.value,
                faceUp: false,
                depth: 0,
                rotation: 0,
                modifiers: [],
                sequence: 0,
                attempts: 0,
                value: value,
                random: true,
                x: 0,
                y: 0,
                width: DEFAULT_CARD_WIDTH,
                height: DEFAULT_CARD_HEIGHT,
            });
            state = CardStateHelper.setCardInStack(state, newCardID);
            cardIDs.push(newCardID);
        });

        return state;
    }

    public static increaseStreak(state: ICardsState, cardScore: number, cardID: string): ICardsState {
        const card = state.cards[cardID];

        if (!cardCastsTo(card, CardType.value) && !cardCastsTo(card, CardType.gold)) {
            return state;
        }

        track('mHighestPlayStreak', state.matchSequence.length + 1, TrackMethod.MAX);

        return {
            ...state,
            matchSequence: [...state.matchSequence, cardColorBySuit[CardStateHelper.getCardSuit(card)]],
            streakScore: state.streakScore + cardScore,
        };
    }

    public static getCardSuit(card: ValueCardData | GoldCardData): Suit {
        return cardCastsTo(card, CardType.gold) ? card.suit : getCardSuit(card.value);
    }

    public static increaseStreakScore(state: ICardsState, cardScore: number): ICardsState {
        return {
            ...state,
            streakScore: state.streakScore + cardScore,
        };
    }

    public static cancelMatchSequence(state: ICardsState): ICardsState {
        if (state.turns === 0) {
            return state;
        }

        return {
            ...state,
            matchSequence: [],
            streakScore: 0,
        };
    }

    public static cancelStreakScore(state: ICardsState): ICardsState {
        return {
            ...state,
            streakScore: 0,
        };
    }

    public static addTurnScore(state: ICardsState, ...score: number[]): ICardsState {
        return {
            ...state,
            turnScore: state.turnScore + score.reduce((a, c) => a + c, 0),
        };
    }

    public static pushUndoHistory(state: ICardsState, cardID: string, cardState: CardState): ICardsState {
        // Ignore first turn.
        if (state.turns === 0) return state;

        return {
            ...state,
            undoHistory: [
                ...state.undoHistory,
                {
                    ...state.cards[cardID],
                    cards: { ...state.cards },
                    modifiers: [],
                    cardState: cardState,
                    matchSequence: state.matchSequence,
                    streakScore: state.streakScore,
                },
            ],
        };
    }

    public static clearUndoHistory(state: ICardsState): ICardsState {
        return {
            ...state,
            undoHistory: [],
        };
    }

    public static checkStreakBonus(state: IGameState): ICardsState {
        let cardsState = state.levelState.cardsState;
        // 1st bonus streak rewards + 1 card.
        // 2nd, 3rd ... bonus streaks reward + 2 cards.
        if (
            cardsState.matchSequence.length > 0 &&
            cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0 &&
            !isLevelFailed(state)
        ) {
            cardsState = CardStateHelper.plusStreakCards(state);
        }
        return cardsState;
    }

    public static setStreakReward(state: ICardsState, reward: StreakReward): ICardsState {
        return {
            ...state,
            streakReward: reward,
        };
    }

    /**
     * Face-up and face-down cards are shuffled separately and concatenated.
     * This is done to prioritize replacing face-up cards with face-down cards when shuffling all value cards.
     * @param state
     */
    public static shuffleValueCards(state: ICardsState): ICardsState {
        const faceUpCardIDs: string[] = [];
        const faceDownCardIDs: string[] = [];

        CardStateHelper.getValueCards(state).forEach(cardID => {
            const behavior = CardStateHelper.getCardBehavior(state, cardID);
            if (behavior.isBlockedByModifier(state, CardModifierType.connected)) {
                return;
            }

            if (behavior.isFaceUp(state)) {
                faceUpCardIDs.push(cardID);
            } else {
                faceDownCardIDs.push(cardID);
            }
        });

        // Note the order of the following shuffled arrays: face-up and face-down are reversed.
        const shuffledCardIDs = [
            ...ArrayHelper.shuffle(faceUpCardIDs, true),
            ...ArrayHelper.shuffle(faceDownCardIDs, true),
        ];
        const shuffledTargetCardIDs = [
            ...ArrayHelper.shuffle(faceDownCardIDs, true),
            ...ArrayHelper.shuffle(faceUpCardIDs, true),
        ];

        const shuffledCards = { ...state.cards };

        shuffledCardIDs.forEach((cardID: string, index: number) => {
            shuffledCards[cardID] = {
                ...shuffledCards[cardID],
                value: (state.cards[shuffledTargetCardIDs[index]] as ValueCardData).value,
            } as ValueCardData;
        });

        return {
            ...state,
            cards: shuffledCards,
        };
    }

    /**
     * Get all value cards in-play unless blocked by fog.
     * @param state
     */
    public static getValueCards(state: ICardsState): string[] {
        return state.inPlay.filter(cardID => {
            if (state.cards[cardID].type !== CardType.value) return false;

            const modifier = state.cards[cardID].modifiers.find(
                modifierData => modifierData.type === CardModifierType.fog
            );
            if (modifier) {
                const behavior = CardStateModifierHelper.getModifierBehavior(state, modifier, cardID);
                return !behavior.isBlocking(state);
            }

            return true;
        });
    }

    public static removeCard(state: ICardsState, cardID: string): ICardsState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        const cards = { ...state.cards };
        delete cards[cardID];
        state = { ...state, cards };

        return state;
    }

    public static addModifierToCard(state: ICardsState, cardID: string, modifierData: ICardModifierData): ICardsState {
        const card = state.cards[cardID];

        if (card.modifiers.some(data => data.type === modifierData.type)) {
            Logger.log(
                `Modifier of type '${modifierData.type}' already exists on card.`,
                LogOrigin.CARD_STATE_HELPER,
                LogStyles.WARNING
            );
            return state;
        }

        return {
            ...state,
            cards: {
                ...state.cards,
                [cardID]: {
                    ...card,
                    modifiers: [...card.modifiers, modifierData],
                },
            },
        };
    }
    public static foldStackCard(
        state: IGameState,
        cardID: string,
        options: {
            queueNext?: boolean;
            skipAnimation?: boolean;
            preserveStreak?: boolean;
        } = {}
    ): IGameState {
        let cardsState = getCardsState(state);
        cardsState = CardStateHelper.pushUndoHistory(cardsState, cardID, CardState.IN_STACK);

        const alreadyFolded: boolean = cardsState.folded.includes(cardID);
        cardsState = CardStateHelper.setCardFolded(cardsState, cardID);

        if (!options.skipAnimation) {
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.FOLD,
                cards: [cardID],
                alreadyFolded,
                queueNext: options.queueNext,
            });
        }

        state = CardStateHelper.updateGameState(state, cardsState);

        state = CardStateModifierHelper.updateModifiers(state, cardID, true);

        cardsState = getCardsState(state);

        if (!options.preserveStreak) {
            cardsState = CardStateHelper.cancelMatchSequence(cardsState);
        }

        return CardStateHelper.updateGameState(state, cardsState);
    }

    /**
     * Get all cards inPlay with options for including or excluding certain card / modifier types
     * @param state
     * @param options
     */
    public static getCardsInPlay(
        state: ICardsState,
        options: {
            ignoreCardTypes?: CardType[];
            requireCardTypes?: CardType[];
            ignoreCardModifierTypes?: CardModifierType[];
            requireCardModifierTypes?: CardModifierType[];
        }
    ): string[] {
        return state.inPlay.filter(cardId => {
            const cardStateData = state.cards[cardId];
            if (
                // If there are types to ignore and this card is of that type then filter out
                (options.ignoreCardTypes && options.ignoreCardTypes.includes(cardStateData.type)) ||
                // If there are types required and this card is not of that type then filter out
                (options.requireCardTypes && !options.requireCardTypes.includes(cardStateData.type)) ||
                // If there are modifier types to ignore and this card has one of these modifiers then filter out
                (options.ignoreCardModifierTypes &&
                    options.ignoreCardModifierTypes.some(modifierTypeToIgnore =>
                        cardStateData.modifiers.some(cardModifier => cardModifier.type === modifierTypeToIgnore)
                    )) ||
                // If there are modifier types required and this card does not have a modifier of that type then filter out
                (options.requireCardModifierTypes &&
                    !options.requireCardModifierTypes.some(requiredModifierType =>
                        cardStateData.modifiers.some(cardModifier => cardModifier.type === requiredModifierType)
                    ))
            ) {
                return false;
            }

            return true;
        });
    }

    public static updateGameState(state: IGameState, cardsState: ICardsState) {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState,
            },
        };
    }

    public static getModifiersOfType(
        cardsState: ICardsState,
        cardID: string,
        ...modifierTypes: CardModifierType[]
    ): ICardModifierData[] {
        return modifierTypes.length === 0
            ? cardsState.cards[cardID].modifiers
            : cardsState.cards[cardID].modifiers.filter(modifiers => modifierTypes.includes(modifiers.type));
    }

    public static hasModifiersOfType(
        cardsState: ICardsState,
        cardID: string,
        ...modifierTypes: CardModifierType[]
    ): boolean {
        return CardStateHelper.getModifiersOfType(cardsState, cardID, ...modifierTypes).length > 0;
    }

    public static getCardStateData(state: IGameState, cardId: string): ICardStateData {
        return state.levelState.cardsState.cards[cardId];
    }

    public static modifierHasCompletedTimer(
        state: IGameState,
        cardId: string,
        modifierType: CardModifierType
    ): boolean {
        return CardStateHelper.getModifiersOfType(state.levelState.cardsState, cardId, modifierType).some(
            modifierData =>
                // If timer exists and
                !isUndefined(modifierData.properties.timer) &&
                // If timer has been reduced to 0
                modifierData.properties.timer <= 0
        );
    }

    public static hasActiveModifierType(state: IGameState, modifierType: CardModifierType) {
        const activeCardIds = CardStateHelper.getActiveCards(state);

        return activeCardIds.some(cardId => {
            const modifiersOfType = CardStateHelper.getModifiersOfType(
                state.levelState.cardsState,
                cardId,
                modifierType
            );
            return modifiersOfType.length > 0;
        });
    }

    /**
     * Returns all in play cards that are not blocked
     * @param state
     */
    public static getActiveCards(state: IGameState) {
        return state.levelState.cardsState.inPlay.filter((cardId: string) => {
            const behavior = CardStateHelper.getCardBehavior(state.levelState.cardsState, cardId);
            return behavior.isBlocked(state.levelState.cardsState) === false;
        });
    }

    public static getAllInPlayCards(state: IGameState): string[] {
        return state.levelState.cardsState.inPlay;
    }

    public static getAllCardIds(state: IGameState): string[] {
        return [
            ...state.levelState.cardsState.inPlay,
            ...state.levelState.cardsState.inStack,
            ...state.levelState.cardsState.folded,
            ...state.levelState.cardsState.discarded,
        ];
    }

    /**
     * Returns the lowest timer value for all "active" TNT modifiers. Active modifiers belong to cards that are face-up
     * and un-obscured. Returns positive infinity if no modifiers are present.
     * @param state
     * @returns number
     */
    public static getLowestActiveTNTModifierTimer(state: IGameState): number {
        const activeTNTCardIds = CardStateHelper.getActiveCards(state);

        const activeTNTModifierData = ArrayHelper.flatten(
            activeTNTCardIds.map(cardId =>
                CardStateHelper.getModifiersOfType(state.levelState.cardsState, cardId, CardModifierType.tnt)
            )
        );

        return activeTNTModifierData.reduce((prevVal, currVal) => {
            const currentTime = currVal.properties.timer;
            return Math.max(0, Math.min(prevVal, currentTime));
        }, Infinity);
    }

    /**
     * Returns the lowest timer value for all "active" tomato bomb modifiers. Active modifiers belong to cards that are face-up
     * and un-obscured. Returns positive infinity if no modifiers are present.
     * @param state
     * @returns number
     */
    public static getLowestActiveTomatoBombModifierTimer(state: IGameState): number {
        const activeTomatoBombCardIds = CardStateHelper.getActiveCards(state);

        const activeTomatoBombModifierData = ArrayHelper.flatten(
            activeTomatoBombCardIds.map(cardId =>
                CardStateHelper.getModifiersOfType(state.levelState.cardsState, cardId, CardModifierType.bomb)
            )
        );

        return activeTomatoBombModifierData.reduce((prevVal, currVal) => {
            const currentTime = currVal.properties.timer;
            return Math.max(0, Math.min(prevVal, currentTime));
        }, Infinity);
    }

    /**
     * Returns true if any card with the supplied type exists in the game state
     * @param state
     * @param cardType type of card to check for
     */
    public static hasCardOfType(state: IGameState, cardType: CardType): boolean {
        for (const cardId in state.levelState.cardsState.cards) {
            if (state.levelState.cardsState.cards[cardId].type === cardType) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns true if the player is currently in a state that may cause a fail-state on the next turn.
     * At the moment this only applies to active TNT modifiers with a value if 1.
     * @param state
     */
    public static failStateIsImminent(state: IGameState): boolean {
        return CardStateHelper.getLowestActiveTNTModifierTimer(state) === 1;
    }

    /**
     * Calls onLevelStarted for each card
     * @param state
     */
    public static onLevelStarted(state: IGameState): IGameState {
        return CardStateHelper.forEachCard(state, (state, cardID) => {
            return CardStateHelper.getCardBehavior(state.levelState.cardsState, cardID).onLevelStarted(state);
        });
    }

    /**
     * Calls onLevelLoaded for each card
     * @param state
     */
    public static onLevelLoaded(state: IGameState): IGameState {
        return CardStateHelper.forEachCard(state, (state, cardID) => {
            return CardStateHelper.getCardBehavior(state.levelState.cardsState, cardID).onLevelLoaded(state);
        });
    }

    /**
     * Updates state with the provided function for each card
     * @param state
     * @param func - state update function
     */
    public static forEachCard(
        state: IGameState,
        func: (state: IGameState, cardID: string, cardData: ICardStateData) => IGameState
    ): IGameState {
        for (const cardID in state.levelState.cardsState.cards) {
            if (Object.prototype.hasOwnProperty.call(state.levelState.cardsState.cards, cardID)) {
                state = func(state, cardID, state.levelState.cardsState.cards[cardID]);
            }
        }
        return state;
    }

    public static getTargets(state: IGameState, cardID: string): string[] {
        let targets = [cardID];
        const connectedModifiers = CardStateHelper.getModifiersOfType(
            state.levelState.cardsState,
            cardID,
            CardModifierType.connected
        );
        connectedModifiers.forEach(modifier => {
            targets.push(modifier.properties.cardID);
        });

        return targets;
    }

    public static wasCardTypeCollected(state: IGameState, cardType: CardType): boolean {
        return state.levelState.cardsState.discarded.some(cardId => {
            return state.levelState.cardsState.cards[cardId].type === cardType;
        });
    }
}

export const getCardsState = (state: IGameState): ICardsState => {
    return { ...state.levelState.cardsState };
};

export const levelContainsCardOfType = (cardsState: ICardsState, cardType: CardType): boolean => {
    return Object.values(cardsState.cards).some(card => card?.type === cardType);
};

export const levelContainsCardWithModifier = (cardsState: ICardsState, modifierType: CardModifierType): boolean => {
    cardsState;
    return Object.values(cardsState.cards).some(card =>
        card?.modifiers.some(modifiers => modifiers.type === modifierType)
    );
};

export const hasModifierOfType = (cardsState: ICardsState, cardID: string, modifierType: CardModifierType): boolean => {
    return cardsState.cards[cardID].modifiers.some(modifiers => modifiers.type === modifierType);
};

export const getStackCardTargetX = (
    index: number,
    stackLength: number = Game.store.getState().levelState.cardsState.inStack.length
) => {
    const reversedIndex = stackLength - 1 - index;
    return CARD_STACK_OFFSET + Math.min(MAX_OPENED_CARDS_IN_STACK - 1, reversedIndex) * CARD_OFFSET_IN_STACK;
};

export const returnFoldedToStack = (cardsState: ICardsState): ICardsState => {
    if ((cardsState.folded.length = 0)) return cardsState;
    const currentCard = CardStateHelper.getCurrentCard(cardsState);

    if (!currentCard) return cardsState;

    cardsState = CardStateHelper.setCardInStack(cardsState, currentCard);

    // Task for animation.
    return CardStateHelper.setStateTaskData(cardsState, {
        task: CardTask.UNDO_FOLD,
        cards: [currentCard],
    });
};

export const isCurrentCardJoker = (state: IGameState): boolean => {
    const cardsState = state.levelState.cardsState;
    if (cardsState.folded.length > 0) {
        const currentCardID: string = CardStateHelper.getCurrentCard(cardsState);
        const currentCardData = cardsState.cards[currentCardID];
        return isCardTypeJoker(currentCardData.type);
    }
    return false;
};

export const isCardTypeJoker = (type: CardType): boolean => {
    return type === CardType.joker || type === CardType.joker_pre_booster;
};
